import React, { useCallback, useMemo, useState } from 'react';
import { min } from 'lodash/fp';

import { Position, PositionModal } from '../../../shared/modal/PositionModal';
import styles from './Fields.module.scss';
import { Text } from '../../../shared/text/Text';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { DeleteButton } from '../../../shared/button/DeleteButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import { Toggle } from '../../../shared/toggle';
import { OpinionPageRef } from '../store';
import { IconButton } from '../../../shared/button/IconButton';
import { DocumentHighlight } from '../../../shared/icons';
import { LongText } from '../../../shared/longtext/LongText';

interface RefModalProps {
    isOpen: boolean;
    closeModal: () => void;
    position: Position | null;
    refLabel: string;
    pageRef: OpinionPageRef[];
    pageRefVerified: boolean;
    updateRefLabel: (val: string) => void;
    updatePageRef: (key: keyof OpinionPageRef, val: string, index: number) => void;
    removePageRef: (index: number) => void;
    verifyPageRefs: (checked: boolean) => void;
    addPageRef: () => void;
}

const FIXED_HEIGHT_ELEMENTS = 120;
const PAGE_REF = 43;

export const RefModal: React.FC<RefModalProps> = ({ closeModal, isOpen, position, updateRefLabel, refLabel, pageRef, pageRefVerified, updatePageRef, verifyPageRefs, removePageRef, addPageRef }) => {
    const [autoFocus, setAutoFocus] = useState<boolean>(false);
    const [openSnippetIndexes, setOpenSnippetIndexes] = useState<number[]>([]);
    const updatePageRefNumber = useCallback((value: string, index: number) => (!isNaN(parseInt(value)) || value === '') && updatePageRef('page', value, index), [updatePageRef]);
    const updatePageRefSnippet = useCallback((value: string, index: number) => updatePageRef('snippet', value, index), [updatePageRef]);
    const height = useMemo(() => `${FIXED_HEIGHT_ELEMENTS + ((openSnippetIndexes.length > 0 ? 3 : min([pageRef.length, 3])!) * PAGE_REF)}px`, [pageRef.length, openSnippetIndexes]);
    const showDelete = useMemo(() => pageRef.length > 1, [pageRef]);
    const addPageReference = useCallback(() => {
        addPageRef();
        setAutoFocus(true);
    }, [addPageRef, setAutoFocus]);

    const getShowSnippetText = useCallback((index: number) => openSnippetIndexes.includes(index), [openSnippetIndexes]);
    const toggleSnippetTextOpen = useCallback((index: number) => {
        const snippetOpen = getShowSnippetText(index);
        if (snippetOpen) {
            setOpenSnippetIndexes(openSnippetIndexes.filter(openIndex => openIndex !== index));
        } else {
            setOpenSnippetIndexes([...openSnippetIndexes, index]);
            const snippetRef = document.getElementById(`page-ref-${index}`);
            snippetRef?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [getShowSnippetText, openSnippetIndexes]);

    const closeAndReset = useCallback(() => {
        setOpenSnippetIndexes([]);
        closeModal();
    }, [closeModal]);

    const removeRef = useCallback((index: number) => {
        const updatedOpenSnippetIndexes = openSnippetIndexes.filter(openIndex => openIndex !== index).map(openIndex => openIndex > index ? openIndex - 1 : openIndex);
        setOpenSnippetIndexes(updatedOpenSnippetIndexes);
        removePageRef(index);
    }, [removePageRef, openSnippetIndexes]);

    return (
        <PositionModal
            isOpen={isOpen}
            closeModal={closeAndReset}
            position={position}
            testId='ref-modal'
            height={height}
            width='300px'
        >
            <>
                <div data-testid='ref-title' className={styles.refInputTitle}>Ref</div>
                <textarea
                    style={{ resize: 'none' }}
                    maxLength={500}
                    className={styles.refInput}
                    placeholder='Add a reference...'
                    value={refLabel}
                    onChange={e => updateRefLabel(e.target.value)}
                    data-testid='ref-input'
                />
                <div className={styles.pageRefHeaderWrapper}>
                    <div data-testid='page-ref-title' className={styles.pageRefTitle}>Page Ref</div>
                    <PlusButton onClick={addPageReference} fontSize={14} />
                </div>
                {!pageRefVerified &&
                    <div className={styles.pageRefVerificationWrapper}>
                        <div>I have checked these page references</div>
                        <Toggle onChange={verifyPageRefs} checked={pageRefVerified} />
                    </div>
                }
                <div className={styles.pageRefsWrapper}>
                    <Scrollable maxHeight={`${3 * PAGE_REF}px`}>
                        {pageRef.map(({ page, snippet }, index) => {
                            const showSnippetText = getShowSnippetText(index);
                            const snippetOverlayText = `${showSnippetText ? 'Close' : 'Open'} Snippet`;
                            return (
                                <div key={index} className={styles.pageRefWrapper}>
                                    <div className={styles.pageRef}>
                                        <Text testId='opinion-page-ref' value={page} onChange={e => updatePageRefNumber(e.target.value, index)} marginBottom='0px' autoFocus={autoFocus} />
                                        <IconButton onClick={() => toggleSnippetTextOpen(index)} icon={DocumentHighlight} margin='0 5px' fontSize={20} overlayText={snippetOverlayText} />
                                        {showDelete ? <DeleteButton fontSize={20} onClick={() => removeRef(index)} testId={`search-row-${index}`} /> : <div className={styles.deletePlaceholder}></div>}
                                    </div>
                                    {showSnippetText &&
                                        <div className={styles.snippetText} id={`page-ref-snippet-${index}`}>
                                            <LongText value={snippet || ''} onChange={value => updatePageRefSnippet(value, index)} maxLength={500} marginBottom='0px' height='74px' />
                                        </div>
                                    }
                                </div>
                            );
                        })}
                    </Scrollable>
                </div>
            </>
        </PositionModal>
    );
};
